import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { EmptyDataParticules } from 'app/shared/molecules/empty-data-particules/empty-data-particules';
import { SvgIcon } from 'app/shared/svg-icon/svg-icon';
import { TooltipDirective } from 'app/shared/directives/tooltip.directive';
import { FunnelStage, SalesSummary } from 'app/core/models/sales-summary.model';
import {
  amountFor,
  biggestDropIndex,
  conversionPercent,
  formatCurrencyAmount,
  FunnelTransition,
  stageSharePercent,
} from 'app/shared/utils/sales-funnel';
import { dashboardWindowLabel } from 'app/home/dashboard-period';

/**
 * The funnel's transitions, named by WHERE the loss happens rather than by the stage it
 * failed to reach.
 *
 * The readout used to print "Mayor caída · Cotizaciones · 6 de 25" — the stage that was
 * reached, with the number that reached it — while the drop it announced was the 19 that
 * did not. Naming the gap between two stages is what makes the sentence say the loss.
 *
 * The second transition ends at the APPROVAL and not at the order on purpose: an order
 * needs no approved quote and can come from an opportunity that was never quoted, so
 * "quoted → order" is not a subset step and its "loss" would not be one.
 */
const DROP_STEPS: { stepKey: string; detailKey: string }[] = [
  { stepKey: 'FUNNEL_STEP_BEFORE_QUOTE', detailKey: 'FUNNEL_STEP_LOST_QUOTE' },
  { stepKey: 'FUNNEL_STEP_QUOTE_TO_APPROVAL', detailKey: 'FUNNEL_STEP_LOST_APPROVAL' },
];

/**
 * One line of the ledger — the indented tree that is the whole card's body.
 *
 * A tree because every node's children sum to it and each level is cut exactly one way;
 * depth is what says so, so it is data and not a CSS class.
 */
export interface LedgerNode {
  titleKey: string;
  /** 0 = a total, 1 = a branch of it, 2 = a branch of a branch. */
  depth: 0 | 1 | 2;
  count: number;
  /**
   * The node's count over the cohort, as a whole percent — the SAME denominator at every
   * depth, so the column can be read straight down. Null on lines that are not subsets of
   * the cohort (the orders closed in the window, and the two buckets from outside it).
   */
  sharePct: number | null;
  /**
   * Width of the line's bar, as a percent of the cohort, or null when the line carries no
   * bar. Only the main stage lines have one: on a sub-line it would compete with its
   * parent, and on a line outside the cohort it would divide two populations.
   */
  barPct: number | null;
  /** Formatted amount, or null when there is none to show in this currency. */
  amount: string | null;
  /**
   * Whether this node has a monetary field at all. False on the opportunity branches:
   * Opportunity carries no amount in the domain, so those cells stay blank rather than
   * saying "sin monto" — which on a money line means "in another currency".
   */
  carriesMoney: boolean;
  /** Where the line links, or null when no listing shows exactly this population. */
  route: string | null;
  /** i18n key of an explanation, shown behind an info glyph beside the label. */
  hintKey?: string;
}

/** The sentence under the hero: the transition that lost the most, spelled out. */
export interface FunnelDropReadout {
  /** i18n key naming where the drop happens ("antes de cotizar"). */
  stepKey: string;
  /** i18n key of the "N de M no llegaron … (P%)" detail. */
  detailKey: string;
  lost: number;
  from: number;
  /** `lost` over `from`, as a whole percent. */
  percent: number;
}

/**
 * The commercial funnel (top of the home dashboard): one cohort of opportunities walked
 * forward through quotes and approvals, followed by the orders closed in the window.
 *
 * Two populations, kept visibly apart:
 *
 *  - **The tree** counts the cohort — the opportunities created in the window — so every
 *    line divides by the same number and the % column ("% de 25") reads straight down.
 *  - **The orders block** counts what CLOSED in the window, and its total is the hero
 *    figure itself. Its three lines are siblings that add up to that total: the cohort's
 *    own orders (the only one with a share), the ones from opportunities outside the
 *    cohort, and the direct ones. None of them is printed as a breakdown of a number it
 *    does not sum to.
 *
 * Money only appears from the quote stage on: Opportunity carries no monetary field.
 */
@Component({
  selector: 'primecontrol-funnel-card',
  imports: [
    CommonModule,
    RouterModule,
    TranslateModule,
    EmptyDataParticules,
    SvgIcon,
    TooltipDirective,
  ],
  templateUrl: './funnel-card.html',
  styleUrl: './funnel-card.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FunnelCard {
  readonly summary = input<SalesSummary | null>(null);
  readonly loading = input<boolean>(false);
  /** The summary request failed: an error state, never the "no data" one. */
  readonly error = input<boolean>(false);

  private _translateService: TranslateService = inject(TranslateService);
  private readonly _langChange = toSignal(this._translateService.onLangChange);

  /**
   * Active i18n language. Reads `_langChange` (not just `currentLang`) so that computeds
   * depending on this recompute on a runtime language switch.
   */
  private readonly lang = computed<string>(() => {
    this._langChange();
    return this._translateService.currentLang || this._translateService.defaultLang || 'es';
  });

  /** The currency the card leads with, or null when no money was found. */
  private readonly primaryCurrency = computed(
    () => this.summary()?.currencies?.find((c) => c.isPrimary) ?? null
  );

  protected readonly isoCode = computed<string>(() => this.primaryCurrency()?.isoCode ?? '');

  /**
   * Orders in the window whose money cannot be placed in any currency, or null when there
   * are none. They are in the order COUNT but in no total, so the footer names the gap.
   * Printed with no symbol: the record does not say which currency it is.
   */
  protected readonly unpricedOrders = computed<{ count: number; amount: string } | null>(() => {
    const unpriced = this.heroOrders()?.unpriced;
    if (!unpriced?.count || unpriced.amount <= 0) {
      return null;
    }
    return {
      count: unpriced.count,
      amount: new Intl.NumberFormat(this.lang(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(unpriced.amount),
    };
  });

  /** A second currency exists whose money is deliberately not folded in. */
  protected readonly otherCurrency = computed<string | null>(() => {
    const others = this.summary()?.currencies?.filter((c) => !c.isPrimary) ?? [];
    return others.length ? others[0].isoCode : null;
  });

  /** Everything that entered the funnel — the divisor of the % column and of every bar. */
  protected readonly cohort = computed<number>(
    () => this.summary()?.funnel?.opportunities?.count ?? 0
  );

  /**
   * Orders closed in the window count as data too: they can come from older
   * opportunities, so a window with no NEW opportunity still has a hero figure
   * and an orders block — the card used to say "no data" while the
   * closed-business card above showed money for the same months.
   */
  protected readonly hasData = computed<boolean>(
    () => this.cohort() > 0 || (this.summary()?.funnel?.orders?.count ?? 0) > 0
  );

  /** The window these numbers actually cover, read off the payload. */
  protected readonly windowLabel = computed<string>(() =>
    dashboardWindowLabel(this.summary()?.closedByMonth ?? [], this.lang())
  );

  /**
   * The population behind the hero figure AND the orders subtotal: every order closed in
   * the window, whatever month its opportunity was opened. Both read this one field, so the
   * subtotal row cannot disagree with the headline.
   */
  private readonly heroOrders = computed<FunnelStage | undefined>(
    () => this.summary()?.funnel?.orders
  );

  /**
   * The hero amount, or null when there is money the card cannot show (see
   * `stageAmount`); the template then says so in words.
   */
  protected readonly wonLabel = computed<string | null>(() => this.stageAmount(this.heroOrders()));

  /** How many orders the hero figure is the sum of. */
  protected readonly orderCount = computed<number>(() => this.heroOrders()?.count ?? 0);

  /** i18n key qualifying the hero amount, or null at zero orders. */
  protected readonly wonOrdersKey = computed<string | null>(() => {
    const count = this.orderCount();
    if (count === 0) {
      return null;
    }
    return count === 1 ? 'FUNNEL_WON_ORDER' : 'FUNNEL_WON_ORDERS';
  });

  /**
   * The transition that lost the most, or null when no transition lost anything (the
   * template then simply states the cohort).
   *
   * Both transitions count OPPORTUNITIES of the same cohort and each stage is a true subset
   * of the one before, so "19 de 25" never crosses two populations.
   */
  protected readonly biggestDrop = computed<FunnelDropReadout | null>(() => {
    const funnel = this.summary()?.funnel;
    if (!funnel) {
      return null;
    }

    const transitions: FunnelTransition[] = [
      { from: funnel.opportunities.count, to: funnel.quoted.count },
      { from: funnel.quoted.count, to: funnel.approvedInCohort.count },
    ];
    const index = biggestDropIndex(transitions);
    if (index < 0) {
      return null;
    }

    const { from, to } = transitions[index];
    const lost = from - to;
    return {
      ...DROP_STEPS[index],
      lost,
      from,
      percent: conversionPercent(from, lost) ?? 0,
    };
  });

  /**
   * The ledger: the whole cohort as a tree that closes at every level.
   *
   * ```
   * Oportunidades          25  100%
   *   Cotizaciones          6   24%
   *     Aprobadas           0    0%
   *     Denegadas           4   16%
   *     Pendientes          2    8%
   *   Sin cotizar          19   76%
   *     Ganadas             2    8%
   *     Perdidas            4   16%
   *     Abiertas           13   52%
   * ```
   *
   * Every split is a set and its complement on the backend, so the arithmetic holds on data
   * nobody has seen yet. Branches whose payload field is missing are dropped rather than
   * zeroed: an older API build is not the same fact as an empty branch.
   */
  protected readonly ledger = computed<LedgerNode[]>(() => {
    const funnel = this.summary()?.funnel;
    if (!funnel) {
      return [];
    }

    const cohort = funnel.opportunities.count;
    const share = (count: number) => conversionPercent(cohort, count);
    const bar = (count: number) => stageSharePercent(count, cohort);

    const nodes: LedgerNode[] = [
      {
        titleKey: 'STAGE_OPPORTUNITIES',
        depth: 0,
        count: cohort,
        sharePct: cohort > 0 ? 100 : null,
        barPct: bar(cohort),
        amount: null,
        carriesMoney: false,
        route: '/customer-support/opportunities',
      },
      {
        titleKey: 'STAGE_QUOTED',
        depth: 1,
        count: funnel.quoted.count,
        sharePct: share(funnel.quoted.count),
        barPct: bar(funnel.quoted.count),
        amount: this.stageAmount(funnel.quoted),
        carriesMoney: true,
        route: '/sales/quotes',
      },
    ];

    // The three ways a quote ends, as children of Cotizaciones.
    if (funnel.deniedInCohort && funnel.openInCohort) {
      nodes.push(
        this.quoteOutcome('STAGE_APPROVED', funnel.approvedInCohort, share),
        this.quoteOutcome('FUNNEL_DENIED_QUOTES', funnel.deniedInCohort, share),
        this.quoteOutcome('FUNNEL_PENDING_QUOTES', funnel.openInCohort, share)
      );
    }

    if (funnel.notQuoted) {
      nodes.push({
        titleKey: 'FUNNEL_NOT_QUOTED',
        depth: 1,
        count: funnel.notQuoted.count,
        sharePct: share(funnel.notQuoted.count),
        barPct: bar(funnel.notQuoted.count),
        amount: null,
        // An opportunity carries no monetary field, so a quote is the earliest point in the
        // flow where money exists at all.
        carriesMoney: false,
        route: '/customer-support/opportunities',
      });

      const split = funnel.notQuotedByDisposition;
      if (split) {
        // Won first: a deal closed without ever being priced is the line that says the
        // process is being skipped. Then lost, then the ones still in play.
        nodes.push(
          this.unquotedOutcome('FUNNEL_WON_UNQUOTED', split.won.count, share),
          this.unquotedOutcome('FUNNEL_LOST_UNQUOTED', split.lost.count, share),
          this.unquotedOutcome('FUNNEL_STILL_OPEN', split.open.count, share)
        );
      }
    }

    return nodes;
  });

  /**
   * The orders block: a subtotal equal to the hero figure, and the three sibling buckets
   * that add up to it (`fromCohort + fromOutsideCohort + direct === orders`, a partition on
   * the backend).
   *
   * The subtotal reads `funnel.orders` — the very field the headline reads — rather than
   * summing the three lines, so headline and subtotal are one number by construction.
   * Only the cohort's own line carries a share and a bar: it is the only one that is a
   * subset of the cohort.
   */
  protected readonly orderLedger = computed<LedgerNode[]>(() => {
    const funnel = this.summary()?.funnel;
    const orders = this.heroOrders();
    const origins = funnel?.ordersByOrigin;
    if (!funnel || !orders || !origins) {
      return [];
    }

    const cohort = funnel.opportunities.count;

    return [
      {
        titleKey: 'FUNNEL_CLOSED_ORDERS',
        depth: 0,
        count: orders.count,
        sharePct: null,
        barPct: null,
        amount: this.wonLabel(),
        carriesMoney: true,
        route: '/sales/sales-orders',
      },
      {
        titleKey: 'FUNNEL_FROM_THIS_COHORT',
        depth: 1,
        count: origins.fromCohort.count,
        sharePct: conversionPercent(cohort, origins.fromCohort.count),
        barPct: stageSharePercent(origins.fromCohort.count, cohort),
        amount: this.stageAmount(origins.fromCohort),
        carriesMoney: true,
        route: '/sales/sales-orders',
      },
      {
        titleKey: 'FUNNEL_FROM_OTHER_OPPORTUNITIES',
        depth: 1,
        count: origins.fromOutsideCohort.count,
        sharePct: null,
        barPct: null,
        amount: this.stageAmount(origins.fromOutsideCohort),
        carriesMoney: true,
        route: '/sales/sales-orders',
        // The bucket tests "outside the cohort" — opened before the window OR deleted — so
        // the label stays generic and the hint carries the precision.
        hintKey: 'FUNNEL_FROM_OTHER_OPPORTUNITIES_HINT',
      },
      {
        titleKey: 'FUNNEL_DIRECT_ORDERS',
        depth: 1,
        count: origins.direct.count,
        sharePct: null,
        barPct: null,
        amount: this.stageAmount(origins.direct),
        carriesMoney: true,
        route: '/sales/sales-orders',
        hintKey: 'FUNNEL_DIRECT_ORDERS_HINT',
      },
    ];
  });

  /** A child of Cotizaciones: one way a quote ended. */
  private quoteOutcome(
    titleKey: string,
    stage: FunnelStage,
    share: (count: number) => number | null
  ): LedgerNode {
    return {
      titleKey,
      depth: 2,
      count: stage.count,
      sharePct: share(stage.count),
      barPct: null,
      amount: this.stageAmount(stage),
      carriesMoney: true,
      route: '/sales/quotes',
    };
  }

  /** A child of Sin cotizar: what became of a never-quoted opportunity. */
  private unquotedOutcome(
    titleKey: string,
    count: number,
    share: (count: number) => number | null
  ): LedgerNode {
    return {
      titleKey,
      depth: 2,
      count,
      sharePct: share(count),
      barPct: null,
      amount: null,
      carriesMoney: false,
      route: '/customer-support/opportunities',
    };
  }

  private currencyId(): number | null {
    return this.primaryCurrency()?.idCurrency ?? null;
  }

  /**
   * A stage's money in the card's currency. An empty stage is worth zero; a stage with
   * records but nothing priced in this currency is NOT zero — it is null, and the
   * template says "sin monto" rather than denying money that exists.
   *
   * @param stage The funnel stage, or undefined before the payload lands.
   */
  private stageValue(stage: FunnelStage | undefined) {
    if (!stage) {
      return null;
    }
    const amount = amountFor(stage.amounts ?? [], this.currencyId());
    if (amount != null) {
      return amount;
    }
    return stage.count === 0 ? 0 : null;
  }

  /**
   * `stageValue`, formatted; null also when no currency is resolved at all, since money
   * the card cannot express is money it does not print.
   */
  private stageAmount(stage: FunnelStage | undefined): string | null {
    const amount = this.stageValue(stage);
    if (amount == null || !this.isoCode()) {
      return null;
    }
    return formatCurrencyAmount(amount, this.lang(), this.isoCode());
  }
}
